import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, InputAdornment, MenuItem, TablePagination } from "@mui/material";
import Loader from "../../../components/common/loader/Loader";
import { StyledWelcomeBanner, StyledWrapper } from "../../dashboard/style";
import {
  StyledBoxStyle,
  StyledCustomerSearch,
  StyledOrdersContainer,
  StyledStatusFilter,
  StyledTableLayout,
} from "../customer-management/style";
import { SHOPPER_STATUS_FILTER_OPTIONS } from "../../../utils/common";
import {
  StyledSearchIcon,
  StyledTypographyText,
} from "../../virtual-locker/style";
import { CompactStatistics } from "../../../components/admin/dashboard/StatisticsComponent";
import OrderTable from "../../../components/common/table/table";
import { PERSONAL_SHOPPER_LIST, SHOPPER_STATUS_CHANGE } from "../../../actions/admin/customer-actions";
import DotIcon from "../../../assets/svg/dot-icon.svg";
import CustomButton from "../../../components/common/button/CustomButton";
import CreateShipmentSvg from "../../../assets/Icons/Create";
import CreateShopper from "../../../components/modals/create-shopper/CreateShopper";
import description from "../../../assets/svg/description.svg";
import process from "../../../assets/svg/process-icon.svg";
import group from "../../../assets/svg/group-des.svg";

const ShopperManagement = () => {
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [shopperAddModal, setShopperAddModal] = useState(false);

  const shopperDetails = useCallback(() => {
    dispatch({
      type: PERSONAL_SHOPPER_LIST,
      payload: { search: debouncedSearch, status: statusFilter, page, limit: rowsPerPage },
    });
  }, [dispatch, debouncedSearch, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    shopperDetails();
  }, [shopperDetails]);

  const { personalShopperList, personalShopperTotal, isLoading } = useSelector(
    (state) => state?.shopper
  );

  const personalShoppers = personalShopperList || [];

  const columns = ["No", "Name", "Email", "Phone", "Shopper-ID", "Email Verification", "Status", "Action"];

  const statusColors = {
    Active: { background: "#00C046" },
    Suspended: { background: "#FF3850" },
    Completed: { background: "#1565C0" },
  };

  // eslint-disable-next-line no-unused-vars
  const getShopperStatus = (item) => {
    if (item?.is_suspended === true) return "Suspended";
    const items = item?.personal_shopper_items || [];
    if (items.length > 0 && items.every((i) => i.status === "approved")) return "Completed";
    return "Active";
  };

  const emailVerificationColors = {
    Yes: { background: "#00C046" },
    No: { background: "red" },
  };

  const allShoppers =
    personalShoppers?.map((item, index) => {
      const status = item?.is_suspended === true ? "Suspended" : "Active";
      const emailVerificationStatus = item?.is_email_verified === true ? "Yes" : "No";

      const shopperIdentifier =
        item?.shopper_id ||
        item?.userId ||
        item?.personal_shopper_id ||
        item?.employee_id ||
        item?.id;

      return {
        id: item.id,
        isSuspended: item.is_suspended,
        orderId: (page - 1) * rowsPerPage + index + 1,
        customer:
          `${item?.first_name || ""} ${item?.last_name || ""}`.trim() || "-",
        email: item.email || "-",
        phone: item.phone_number || item.phone || "-",
        lockerId: shopperIdentifier ? (
          shopperIdentifier
        ) : (
          <span
            style={{
              background: "#FF8500",
              fontWeight: "bold",
              display: "inline-flex",
              color: "white",
              padding: "2px 8px 2px 6px",
              borderRadius: "20px",
              minWidth: "90px",
              textAlign: "center",
              alignItems: "center",
              gap: 4,
            }}
          >
            <img src={DotIcon} alt="" />
            Unassigned
          </span>
        ),
        emailVerification: (
          <span
            style={{
              background:
                emailVerificationColors[emailVerificationStatus]?.background,
              fontWeight: "bold",
              display: "inline-flex",
              color: "white",
              padding: "2px 8px 2px 6px",
              borderRadius: "20px",
              minWidth: "50px",
              textAlign: "center",
              alignItems: "center",
              gap: 5,
            }}
          >
            <img src={DotIcon} alt="" />{emailVerificationStatus}
          </span>
        ),
        status: (
          <span
            style={{
              background: statusColors[status]?.background,
              fontWeight: "bold",
              display: "inline-flex",
              color: "white",
              padding: "2px 8px 2px 6px",
              borderRadius: "20px",
              minWidth: "65px",
              textAlign: "center",
              alignItems: "center",
              gap: 5,
            }}
          >
            <img src={DotIcon} alt="" />
            {status}
          </span>
        ),
      };
    }) || [];

const statisticsData = [
    {
      title: "Total Shoppers",
      value: personalShopperTotal,
      icon: description
    },
    {
      title: "Active Shoppers",
      value: personalShoppers.filter(user => user.is_suspended === false).length,
      icon: process
    },
    {
      title: "Pending Verification",
      value: personalShoppers.filter(user => user.is_email_verified === false).length,
      icon: group
    }
  ];
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  return (
    <StyledWrapper>
      <StyledWelcomeBanner>
        <CompactStatistics idDataList={statisticsData || []} />
      </StyledWelcomeBanner>

      <StyledTableLayout>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            width: "100%",
          }}
        >
          <StyledTypographyText>SHOPPER MANAGEMENT</StyledTypographyText>
          <Box
            sx={{
              width: { xs: "100%", sm: "auto" },
              flexShrink: 0,
              ml: { xs: 0, sm: "auto" },
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            <CustomButton
              startIcon={<CreateShipmentSvg width={20} height={20} />}
              btnLabel="Add Shopper"
              height="44px"
              onClick={() => setShopperAddModal(true)}
            />
          </Box>
        </Box>

        <StyledBoxStyle>
          <StyledCustomerSearch
            placeholder="Search by name, email, phone"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StyledSearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <StyledStatusFilter
            value={statusFilter}
            onChange={handleStatusFilterChange}
            displayEmpty
            inputProps={{ "aria-label": "Filter by status" }}
          >
            {SHOPPER_STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </StyledStatusFilter>
        </StyledBoxStyle>

        <StyledOrdersContainer>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <OrderTable
                columns={columns}
                rows={allShoppers}
                customerDetails={shopperDetails}
                columnsToHide={["isSuspended", "id"]}
                statusActionType={SHOPPER_STATUS_CHANGE}
                showCustomerDetailLink
                detailLinkPrefix="/admin/shoppers"
              />
              <TablePagination
                component="div"
                count={personalShopperTotal || 0}
                page={page - 1}
                onPageChange={(event, newPage) => setPage(newPage + 1)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </StyledOrdersContainer>
      </StyledTableLayout>

      {shopperAddModal && (
        <CreateShopper
          open={shopperAddModal}
          handleClose={() => setShopperAddModal(false)}
          shopperDetails={shopperDetails}
        />
      )}
    </StyledWrapper>
  );
};

export default ShopperManagement;
